"use client";
import React, { useRef, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import "../../../css/ContactusPage.css";
import { useForm } from "react-hook-form";
import { IoLocationSharp } from "react-icons/io5";
import { LuPhone } from "react-icons/lu";
import { Default } from "../../layouts/default";
import { axiosAuth } from "../../../utils/axiosInstance";
import { Toast } from "primereact/toast";
import { CiLocationOn } from "react-icons/ci";
import { CiMail } from "react-icons/ci";
import { FiPhoneCall } from "react-icons/fi";
import { Link } from "react-router-dom";
import LazyImage from "../../common/LazyImage";
import bannerBv from "../../../image/banner-bv.png";

const ContactusPage = () => {
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const toast = useRef(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => {
        setSuccessMsg("");
      }, 2000);

      return () => clearTimeout(timer); // cleanup
    }
  }, [successMsg]);

  const onSubmit = async (data) => {
    setLoading(true);
    const payLoad = {
      name: data.name,
      email: data.email,
      mobile: data.mobile,
      message: data.message,
    };

    try {
      const response = await axiosAuth.post("/contact-us", payLoad);

      if (response.status === 200 || response.statusText === "OK") {
        toast.current?.show({
          severity: "success",
          summary: "Message Sent",
          detail: "Your query has been submitted successfully.",
          life: 3000,
        });
        setSuccessMsg(
          "Your query has been submitted. Our team will contact you as soon as possible.",
        );
        reset();
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Failed",
          detail: "Submission failed. Please try again.",
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Something went wrong. Please try again later.",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Default>
      <Helmet>
        <title>Contact Book Venue | Sports Venue Booking Suppor</title>

        <meta
          name="description"
          content="Contact Book Venue for sports venue booking support, cricket ground booking, football turf booking, badminton court booking, payment support, refunds, and venue partnership inquiries."
        />

        <meta name="robots" content="index, follow" />

        <Link rel="canonical" to="https://bookvenue.app/contact-us" />
      </Helmet>
      <div className="contact-us-page">
        <header className="contact-hero">
          <div className="bv-container bv-container--wide contact-hero__inner">
            <nav className="contact-breadcrumb bv-reveal" aria-label="Breadcrumb">
              <Link to="/" className="contact-breadcrumb__link">
                <i className="fa-solid fa-house" aria-hidden="true" />
                Home
              </Link>
              <span className="contact-breadcrumb__sep" aria-hidden="true">
                <i className="fa-solid fa-chevron-right" />
              </span>
              <span className="contact-breadcrumb__current" aria-current="page">
                Contact Us
              </span>
            </nav>

            <div className="contact-hero__body">
              <div className="contact-hero__content">
                <span className="contact-hero__eyebrow">
                  <i className="fa-solid fa-headset" aria-hidden="true" />
                  We are here to help
                </span>
                <h1 className="contact-hero__title">
                  Get in Touch with <span className="gradientText">BookVenue</span>
                </h1>
                <p className="contact-hero__lede">
                  Need support with bookings, venue partnerships, payments, or refunds?
                  Our team is ready to help you every step of the way.
                </p>
              </div>

              <div className="contact-hero__visual">
                <LazyImage
                  className="contact-hero__img"
                  src={bannerBv}
                  alt="Support team for sports venue booking"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </header>

        <div className="content blog-details contact-group">
          <div className="container">
            <h1 className="explore-head">Get in Touch with BookVenue</h1>
            <p className="text-center text-para">
              We usually respond within 24 hours for support, booking or
              partnerships.
            </p>
            <div className="row mb-5">
              <div className="col-12 col-sm-12 col-md-6 col-lg-4">
                <div className="d-flex justify-content-start align-items-center details">
                  <span className="d-flex justify-content-center align-items-center contact-icon">
                    <CiMail />
                  </span>
                  <div className="info">
                    <h4>Email Address</h4>
                    <p>
                      <Link to="mailto:admin@bookvenue.app">
                        admin@bookvenue.app
                      </Link>
                    </p>
                  </div>
                </div>
              </div>
              <div className="contact-card">
                <span className="contact-card-icon">
                  <FiPhoneCall />
                </span>
                <div className="contact-card-content">
                  <h4>Phone Number</h4>
                  <p>
                    <Link to="tel:+917500872014">+91-7500872014</Link>
                  </p>
                </div>
              </div>
              <div className="contact-card">
                <span className="contact-card-icon">
                  <CiLocationOn />
                </span>
                <div className="contact-card-content">
                  <h4>Office Location</h4>
                  <p>
                    BookVenue Sujok Building, 2nd floor, Mansarovar Colony,
                    Ballupur, Dehradun, Uttarakhand 248001
                  </p>
                </div>
              </div>
            </div>
          </div>
          <section className="section contact-dull-bg">
            <div className="container">
              <h2 className="text-center mb-5">
                Reach Out to BookVenue
              </h2>
              <div className="contact-section-grid">
                <form
                  className="contact-us-form"
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="first-name" className="form-label">
                        First Name*
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="first-name"
                        placeholder="Enter First Name"
                        {...register("name", {
                          required: "First name is required",
                        })}
                      />
                      {errors.name && (
                        <small className="text-danger">
                          {errors.name.message}
                        </small>
                      )}
                    </div>
                    <div className="form-group">
                      <label htmlFor="last-name" className="form-label">
                        Last Name*
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="last-name"
                        placeholder="Enter Last Name"
                        {...register("lastName", {
                          required: "Last name is required",
                        })}
                      />
                      {errors.lastName && (
                        <small className="text-danger">
                          {errors.lastName.message}
                        </small>
                      )}
                    </div>
                    <div className="form-group">
                      <label htmlFor="e-mail" className="form-label">
                        Email*
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        id="e-mail"
                        placeholder="Enter Email Address"
                        {...register("email", {
                          required: "Email is required",
                          pattern: {
                            value: /^\S+@\S+$/i,
                            message: "Invalid email format",
                          },
                        })}
                      />
                      {errors.email && (
                        <small className="text-danger">
                          {errors.email.message}
                        </small>
                      )}
                    </div>
                    <div className="form-group">
                      <label htmlFor="phone" className="form-label">
                        Phone Number*
                      </label>
                      <input
                        type="tel"
                        className="form-control"
                        id="phone"
                        placeholder="Enter Phone Number"
                        maxLength={10}
                        onInput={(e) => {
                          e.target.value = e.target.value.replace(/[^0-9]/g, "");
                        }}
                        {...register("mobile", {
                          required: "Phone number is required",
                          validate: (value) => {
                            if (value === "") return true;
                            if (value.length !== 10)
                              return "Phone number must be 10 digits";
                            return true;
                          },
                        })}
                      />
                      {errors.mobile && (
                        <small className="text-danger">
                          {errors.mobile.message}
                        </small>
                      )}
                    </div>
                  </div>

                  <div className="form-group form-group-fullwidth">
                    <label htmlFor="comments" className="form-label">
                      Comments*
                    </label>
                    <textarea
                      className="form-control"
                      id="comments"
                      rows="6"
                      placeholder="Enter Comments"
                      maxLength={250}
                      {...register("message", {
                        required: "Comment is required",
                        maxLength: {
                          value: 250,
                          message: "Maximum 250 characters allowed",
                        },
                      })}
                    ></textarea>
                    {errors.message && (
                      <small className="text-danger">
                        {errors.message.message}
                      </small>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="btn btn-secondary contact-submit-btn"
                    disabled={loading}
                  >
                    {loading ? "Sending..." : "Submit"}
                  </button>
                </form>

                <div className="contact-aside-panel">
                  <div className="contact-aside-card">
                    <h3>Contact Details</h3>
                    <div className="contact-aside-item">
                      <span className="contact-aside-icon">
                        <CiMail />
                      </span>
                      <div>
                        <h4>Email</h4>
                        <p>
                          <Link to="mailto:admin@example.com">admin@bookvenue.app</Link>
                        </p>
                      </div>
                    </div>
                    <div className="contact-aside-item">
                      <span className="contact-aside-icon">
                        <FiPhoneCall />
                      </span>
                      <div>
                        <h4>Phone</h4>
                        <p>
                          <Link to="tel:+917500872014">+91-7500872014</Link>
                        </p>
                      </div>
                    </div>
                    <div className="contact-aside-item">
                      <span className="contact-aside-icon">
                        <CiLocationOn />
                      </span>
                      <div>
                        <h4>Location</h4>
                        <p>
                          BookVenue Sujok Building, 2nd floor, Mansarovar Colony,
                          Ballupur, Dehradun, Uttarakhand 248001
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="google-maps contact-map-card">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3443.6321522987473!2d78.00611638142615!3d30.332979309750396!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39092a04fd840c5b%3A0x8824c50e1b82e75c!2sBallupur%20Chowk%2C%20Ballupur%2C%20Dehradun%2C%20Uttarakhand%20248001!5e0!3m2!1sen!2sin!4v1774503575269!5m2!1sen!2sin"
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="BookVenue Office Location"
                      style={{ width: "100%", height: "340px", border: "0px", borderRadius: "18px" }}
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
      <Toast ref={toast} position="bottom-right" />
    </Default>
  );
};

export default ContactusPage;
